import { useRef } from 'react';

import type { ICourseDates, ICreateCourseRequest } from '@treneo/models';
import { DEFAULT_TIMEZONE } from '@treneo/shared/constants';
import { useDateTimeConfig } from '@treneo/shared/contexts';
import { useSubmitGuard } from '@treneo/shared/forms';
import { useAppTranslations } from '@treneo/shared/i18n';
import { logger } from '@treneo/shared/logger';
import { toast } from '@treneo/shared/ui';

import { coursesApi, useCreateCourseMutation } from '../../../data';
import { convertCourseDatesToUtcFormat } from '../../../utils/course-dates-utils';

import type { CourseFormData } from '../shared/types';

import { CreateCourseModal } from './create-course-modal';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CreateCourseModalContainer = ({ open, onClose }: Readonly<Props>) => {
  const { t } = useAppTranslations();
  const { dateFormat, timeFormat } = useDateTimeConfig();
  const toastShownRef = useRef<string | null>(null);
  const { guard, reset } = useSubmitGuard();

  const createCourseMutation = useCreateCourseMutation();

  const handleSubmit = guard((submissionData: CourseFormData) => {
    if (createCourseMutation.isPending) {
      return;
    }

    try {
      const request: ICreateCourseRequest = {
        title: submissionData.title,
        description: submissionData.description ?? '',
        timezone: submissionData.timezone ?? DEFAULT_TIMEZONE,
        maxParticipants: submissionData.maxParticipants ?? 20,
        expectedParticipants: submissionData.expectedParticipants ?? 10,
        daDatacenterIds: submissionData.daDataCenterIds ?? [],
        daOrganizationId: submissionData.daOrganizationId ?? 0,
        wantToBeCoordinator: submissionData.wantToBeCoordinator ?? true,
        isDemo: submissionData.isDemo ?? false,
        trainerUserId: submissionData.trainerUserId ?? '',
        trainerEmail: submissionData.trainerEmail ?? '',
        courseDates: []
      };

      if (submissionData.courseDates && submissionData.courseDates.length > 0) {
        const utcCourseDates = convertCourseDatesToUtcFormat(
          submissionData.courseDates,
          submissionData.timezone,
          dateFormat,
          timeFormat
        );
        const validUtcDates = utcCourseDates.filter(
          dateEntry =>
            typeof dateEntry.startDate === 'string' &&
            typeof dateEntry.endDate === 'string' &&
            dateEntry.startDate.length > 0 &&
            dateEntry.endDate.length > 0
        );
        request.courseDates = validUtcDates.map(dateEntry => ({
          startDate: new Date(dateEntry.startDate) as Date,
          endDate: new Date(dateEntry.endDate) as Date
        })) as ICourseDates[];
      }

      if (request.courseDates.length === 0) {
        const now = new Date();
        const futureDate = new Date(now.getTime() + 8 * 60 * 60 * 1000);
        request.courseDates = [
          {
            startDate: now as Date,
            endDate: futureDate as Date
          }
        ] as ICourseDates[];
      }

      createCourseMutation.mutate(request, {
        onSettled: reset,
        onSuccess: data => {
          const rawId = data?.id;
          if (rawId == null || rawId === undefined) {
            toast.success(t('courseForm.courseCreatedSuccessfully'));
            onClose();
            return;
          }
          const courseId = String(rawId);
          if (toastShownRef.current === courseId) {
            return;
          }
          toast.success(t('courseForm.courseCreatedSuccessfully'));
          toastShownRef.current = courseId;

          const trainerEmailFromForm = (request.trainerEmail ?? '').trim();
          const pendingFromList = (submissionData.pendingTrainerInviteEmails ?? []).filter(
            (e: string) => typeof e === 'string' && e.trim().length > 0
          );
          const seen = new Set<string>();
          const emailsToInvite: string[] = [];
          if (trainerEmailFromForm.length > 0) {
            const lower = trainerEmailFromForm.toLowerCase();
            if (!seen.has(lower)) {
              seen.add(lower);
              emailsToInvite.push(trainerEmailFromForm);
            }
          }
          for (const e of pendingFromList) {
            const trimmed = e.trim();
            const lower = trimmed.toLowerCase();
            if (!seen.has(lower)) {
              seen.add(lower);
              emailsToInvite.push(trimmed);
            }
          }
          if (emailsToInvite.length === 0) {
            onClose();
            return;
          }

          const sendInvites = async () => {
            const origin = globalThis.location.origin;
            let successCount = 0;
            let _failCount = 0;
            for (const email of emailsToInvite) {
              const trimmedEmail = email.trim();
              if (!trimmedEmail) {
                continue;
              }
              const result = await coursesApi.inviteTrainerToCourse({
                courseId,
                email: trimmedEmail,
                origin
              });
              if (result.isSuccess) {
                successCount += 1;
              } else {
                _failCount += 1;
                toast.error(t('trainers.invitationFailed'));
              }
            }
            if (successCount > 0) {
              toast.success(t('trainers.invitationSent'));
            }
            onClose();
          };

          void sendInvites();
        }
      });
    } catch (error) {
      reset();
      logger.error('Error preparing course creation request:', error);
    }
  });

  return (
    <CreateCourseModal
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={createCourseMutation.isPending}
      isSuccess={createCourseMutation.isSuccess}
    />
  );
};
